import React from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  InputBase,
  Badge,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
  Divider,
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import moment from "moment-timezone";
import { SIDEBAR_WIDTH } from "./Sidebar";
import {
  tokens,
  THEMES,
  getThemeMode,
  setThemeMode,
  persistThemeMode,
} from "../../theme";
import { endPoints } from "../../constant/Environment";
import { getAllData } from "../../Utility/API";

// Read the logged-in user from the stored login/OTP response.
function getStoredUser() {
  try {
    const raw = sessionStorage.getItem("user");
    if (!raw || raw === "undefined") return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

export default function Topbar({ onMenuClick }) {
  const navigate = useNavigate();
  const [anchor, setAnchor] = React.useState(null);
  const [notifAnchor, setNotifAnchor] = React.useState(null);
  const [themeAnchor, setThemeAnchor] = React.useState(null);
  const activeTheme = getThemeMode();
  const [errorLogs, setErrorLogs] = React.useState([]);

  const fetchErrorLogs = React.useCallback(async () => {
    try {
      const response = await getAllData(endPoints.api.GET_ERROR_LOG_24H);
      if (response?.status === "success") {
        setErrorLogs(response.data || []);
      }
    } catch (e) {
      console.error("error log fetch failed", e);
    }
  }, []);

  React.useEffect(() => {
    fetchErrorLogs();
    // Refresh the notification feed every 60s.
    const timer = setInterval(fetchErrorLogs, 60000);
    return () => clearInterval(timer);
  }, [fetchErrorLogs]);

  const stored = getStoredUser();
  const u = stored?.data || stored || {};
  const fullName =
    [u.name, u.surname].filter(Boolean).join(" ").trim() ||
    u.login ||
    u.username ||
    "Admin";
  const subtitle = u.login || u.email || "Administrator";
  const initials =
    fullName
      .split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "AD";

  const logout = () => {
    setAnchor(null);
    sessionStorage.clear();
    localStorage.clear();
    persistThemeMode(); // a device preference, not session data
    navigate("/login");
    window.location.reload();
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: { md: `calc(100% - ${SIDEBAR_WIDTH}px)` },
        ml: { md: `${SIDEBAR_WIDTH}px` },
        bgcolor: tokens.appBar,
        // AppBar would otherwise inherit primary.contrastText for its icons,
        // which is invisible on the light theme's near-white bar.
        color: tokens.text,
        backdropFilter: "blur(12px)",
        borderBottom: `1px solid ${tokens.border}`,
      }}
    >
      <Toolbar sx={{ gap: 1.5 }}>
        <IconButton
          color="inherit"
          edge="start"
          onClick={onMenuClick}
          sx={{ display: { md: "none" } }}
        >
          <MenuRoundedIcon />
        </IconButton>

        {/* Search */}
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            alignItems: "center",
            gap: 1,
            px: 1.5,
            height: 40,
            minWidth: 280,
            borderRadius: 2.5,
            bgcolor: tokens.surface2,
            border: `1px solid ${tokens.border}`,
          }}
        >
          <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
          <InputBase
            placeholder="Search players, transactions, games…"
            sx={{ color: "text.primary", fontSize: 14, flex: 1 }}
          />
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title="Theme">
          <IconButton color="inherit" onClick={(e) => setThemeAnchor(e.currentTarget)}>
            <PaletteRoundedIcon />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={themeAnchor}
          open={Boolean(themeAnchor)}
          onClose={() => setThemeAnchor(null)}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          PaperProps={{ sx: { mt: 1, minWidth: 228 } }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2">Theme</Typography>
            <Typography variant="caption" color="text.secondary">
              Reloads to apply
            </Typography>
          </Box>
          <Divider sx={{ borderColor: tokens.border }} />
          {THEMES.map((t) => (
            <MenuItem
              key={t.id}
              selected={t.id === activeTheme}
              onClick={() => {
                setThemeAnchor(null);
                setThemeMode(t.id);
              }}
              sx={{ gap: 1.2, py: 1 }}
            >
              {/* palette preview: page bg, primary, accent */}
              <Box
                sx={{
                  display: "flex",
                  borderRadius: "5px",
                  overflow: "hidden",
                  border: `1px solid ${tokens.border}`,
                  flex: "none",
                }}
              >
                {t.swatch.map((c) => (
                  <Box key={c} sx={{ width: 10, height: 18, bgcolor: c }} />
                ))}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>
                  {t.label}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: tokens.textDim2 }} noWrap>
                  {t.hint}
                </Typography>
              </Box>
              {t.id === activeTheme && (
                <CheckRoundedIcon sx={{ fontSize: 16, color: tokens.primary, flexShrink: 0 }} />
              )}
            </MenuItem>
          ))}
        </Menu>

        <Tooltip title="Notifications">
          <IconButton color="inherit" onClick={(e) => setNotifAnchor(e.currentTarget)}>
            <Badge badgeContent={errorLogs.length} color="error" max={99}>
              <NotificationsRoundedIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={notifAnchor}
          open={Boolean(notifAnchor)}
          onClose={() => setNotifAnchor(null)}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          PaperProps={{ sx: { mt: 1, width: 360, maxHeight: 440 } }}
        >
          <Box sx={{ px: 2, py: 1.25, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography variant="subtitle2">Error Log</Typography>
              <Typography variant="caption" color="text.secondary">Last 24 hours</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {errorLogs.length} {errorLogs.length === 1 ? "issue" : "issues"}
            </Typography>
          </Box>
          <Divider sx={{ borderColor: tokens.border }} />
          {errorLogs.length === 0 ? (
            <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary">
                No errors logged
              </Typography>
            </Box>
          ) : (
            errorLogs.slice(0, 20).map((log) => (
              <MenuItem
                key={log.id}
                onClick={() => setNotifAnchor(null)}
                sx={{ alignItems: "flex-start", py: 1.1, whiteSpace: "normal", gap: 1.2, borderBottom: `1px solid ${tokens.border}`, "&:last-of-type": { borderBottom: "none" } }}
              >
                <ReportProblemRoundedIcon sx={{ fontSize: 18, color: tokens.danger, mt: 0.3, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.2 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                      {log.errorType || "Error"}
                    </Typography>
                    {log.errorCode && (
                      <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: 10.5, color: tokens.textDim2 }}>
                        #{log.errorCode}
                      </Typography>
                    )}
                    {log.userId && (
                      <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: 10.5, color: tokens.textDim2 }}>
                        · user {log.userId}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 12, color: tokens.textDim, lineHeight: 1.35 }}>
                    {log.errorMessage}
                  </Typography>
                  {log.createdOn && (
                    <Typography sx={{ fontSize: 10.5, color: tokens.textDim2, mt: 0.4 }}>
                      {moment.utc(log.createdOn).format("YYYY-MM-DD HH:mm")}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))
          )}
          <Divider sx={{ borderColor: tokens.border }} />
          <MenuItem
            onClick={() => { setNotifAnchor(null); navigate("/operate/error-log"); }}
            sx={{ justifyContent: "center", py: 1, fontSize: 12.5, fontWeight: 600, color: tokens.primary }}
          >
            View all errors
          </MenuItem>
        </Menu>

        <Tooltip title="Account">
          <IconButton onClick={(e) => setAnchor(e.currentTarget)} sx={{ p: 0.5, ml: 0.5 }}>
            <Avatar
              sx={{
                width: 34,
                height: 34,
                background: `linear-gradient(135deg, ${tokens.primary}, ${tokens.secondary})`,
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              {initials}
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          PaperProps={{ sx: { mt: 1, minWidth: 200 } }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2">{fullName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
          <Divider sx={{ borderColor: tokens.border }} />
          <MenuItem onClick={() => setAnchor(null)}>
            <ListItemIcon><PersonRoundedIcon fontSize="small" /></ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem onClick={() => { setAnchor(null); navigate("/AccessList"); }}>
            <ListItemIcon><SettingsRoundedIcon fontSize="small" /></ListItemIcon>
            Access List
          </MenuItem>
          <Divider sx={{ borderColor: tokens.border }} />
          <MenuItem onClick={logout}>
            <ListItemIcon><LogoutRoundedIcon fontSize="small" /></ListItemIcon>
            Log out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
